/**
 * 2-D dashboard grid geometry + layout helpers (react-grid-layout).
 *
 * The dashboard renders on a 12-column responsive grid with vertical
 * compaction so widgets pack upward with no gaps. Saved user layouts persist
 * exact x/y/w/h.
 *
 * Geometry *defaults* live only on the backend
 * (`shared/services/dashboard_widget_registry.py`): the resolved layout carries
 * w/h per widget, and the widget catalog carries `default_w`/`default_h` for a
 * freshly-added one. Nothing here re-derives size→width or category→height.
 * See docs/platform/dashboard-widget-registry.md.
 */

import type { Layout } from 'react-grid-layout';
import type { DashboardWidget } from '../../../generated/api/models/dashboardWidget';
import type { ResolvedWidget } from '../../../generated/api/models/resolvedWidget';
import { configDefaults } from './widgetConfig';
import type { WidgetPlacementInput } from './useDashboardLayout';

export const GRID_COLS = 12;
/** Pixel height of one grid row unit. */
export const GRID_ROW_HEIGHT = 76;
/** [horizontal, vertical] gap between cells, in px. */
export const GRID_MARGIN: [number, number] = [16, 16];
/** No extra padding inside the grid container — the page already pads. */
export const GRID_CONTAINER_PADDING: [number, number] = [0, 0];

/**
 * Two breakpoints only: the full 12-column desktop grid, and a single-column
 * stack on narrow screens (≤ md / mobile 375px). Both layouts are authored
 * explicitly — see `buildMobileGridLayout` for why `xs` cannot be left to RGL.
 */
export const GRID_BREAKPOINTS = { lg: 768, xs: 0 };
export const GRID_COLS_BY_BREAKPOINT = { lg: GRID_COLS, xs: 1 };
/** Only the desktop (`lg`) geometry is persisted; `xs` is derived from it. */
export const GRID_PRIMARY_BREAKPOINT = 'lg';
/** Column count at the single-column (`xs`) breakpoint. */
export const MOBILE_GRID_COLS = GRID_COLS_BY_BREAKPOINT.xs;

export const WIDGET_MIN_W = 3;
export const WIDGET_MIN_H = 2;

/**
 * Cell height (rows) an *empty* widget collapses to, so a nearly-empty tenant
 * doesn't see cards that are ~60% whitespace. Tall enough for the shared
 * onboarding empty slot (icon + one-line message + optional CTA) plus card
 * chrome, but well under a data-state widget's authored height. Only applied in
 * the read-only grid (never persisted); the editor keeps full authored heights.
 * See `collapseEmptyWidgets` + `widgetEmptyReporter`.
 */
export const EMPTY_WIDGET_H = 3;

/**
 * Place a widget picked from the catalog at the bottom of the current layout.
 *
 * Geometry comes from the catalog's server-resolved `default_w`/`default_h` —
 * the same values the backend layout resolver would assign — so a freshly-added
 * widget is sized identically to one that arrives in a resolved layout.
 */
export function buildAddedWidget(
  widget: DashboardWidget,
  existing: ResolvedWidget[]
): ResolvedWidget {
  const bottom = existing.reduce((max, p) => Math.max(max, (p.y ?? 0) + p.h), 0);
  return {
    key: widget.key,
    module_id: widget.module_id,
    title_i18n_key: widget.title_i18n_key,
    size: widget.default_size,
    category: widget.category,
    w: widget.default_w,
    h: widget.default_h,
    x: 0,
    y: bottom,
    config_fields: widget.config_fields,
    // Seed with declared defaults so the widget renders correctly before the
    // first save round-trips the backend-coerced values.
    config: configDefaults(widget.config_fields),
  };
}

/** A widget has a fully-pinned position (saved user layout). */
function hasPosition(w: ResolvedWidget): boolean {
  return w.x !== null && w.x !== undefined && w.y !== null && w.y !== undefined;
}

/**
 * Build the desktop (`lg`) react-grid-layout from resolved widgets.
 *
 * - When every widget carries an explicit x/y (a saved user layout) the
 *   geometry is used verbatim so the arrangement reloads exactly.
 * - Otherwise (module/template defaults, which omit position) widgets are
 *   shelf-packed left-to-right; `compactType="vertical"` then settles them
 *   upward into the smallest gap-free arrangement.
 */
export function buildGridLayout(widgets: ResolvedWidget[]): Layout[] {
  const allPositioned = widgets.length > 0 && widgets.every(hasPosition);

  if (allPositioned) {
    return widgets.map((widget) => ({
      i: widget.key,
      x: widget.x as number,
      y: widget.y as number,
      w: clampWidth(widget.w),
      h: clampHeight(widget.h),
      minW: WIDGET_MIN_W,
      minH: WIDGET_MIN_H,
      maxW: GRID_COLS,
    }));
  }

  // Shelf-pack: flow across the row, wrapping when the next widget overflows.
  let cursorX = 0;
  let cursorY = 0;
  let rowHeight = 0;
  return widgets.map((widget) => {
    const w = clampWidth(widget.w);
    const h = clampHeight(widget.h);
    if (cursorX + w > GRID_COLS) {
      cursorX = 0;
      cursorY += rowHeight;
      rowHeight = 0;
    }
    const item: Layout = {
      i: widget.key,
      x: cursorX,
      y: cursorY,
      w,
      h,
      minW: WIDGET_MIN_W,
      minH: WIDGET_MIN_H,
      maxW: GRID_COLS,
    };
    cursorX += w;
    rowHeight = Math.max(rowHeight, h);
    return item;
  });
}

// The backend always materialises w/h onto a ResolvedWidget, but a layout
// persisted to IndexedDB before the 2-D grid rework rehydrates without them.
// A NaN width/height breaks react-grid-layout for the whole dashboard, so an
// absent value collapses to the minimum cell size; the refetch that follows
// replaces it with the server's real geometry.
//
// Both clamps also enforce the `minW <= w` / `minH <= h` invariant RGL's
// GridItem asserts — it logs "minWidth larger than item width/maxWidth"
// otherwise — since every item this module emits carries minW/minH.
function clampWidth(w: number | null | undefined): number {
  if (!Number.isFinite(w)) return WIDGET_MIN_W;
  return Math.min(Math.max(w as number, WIDGET_MIN_W), GRID_COLS);
}

function clampHeight(h: number | null | undefined): number {
  if (!Number.isFinite(h)) return WIDGET_MIN_H;
  return Math.max(h as number, WIDGET_MIN_H);
}

/**
 * Build the single-column (`xs`) react-grid-layout from resolved widgets.
 *
 * This MUST be authored rather than left to react-grid-layout. When a
 * breakpoint's layout is absent RGL synthesises one from `lg` via
 * `correctBounds()`, which clamps `w` down to the breakpoint's column count (1)
 * but leaves `minW` at `WIDGET_MIN_W` (3). GridItem then asserts `minW <= w` and
 * logs `Failed prop type: minWidth larger than item width/maxWidth` for every
 * widget at 375px. Supplying `xs` explicitly means RGL uses it verbatim, so the
 * per-breakpoint `minW`/`maxW` are ours to keep consistent with `w`.
 *
 * Widgets stack full-width in desktop reading order (row-major: top row first,
 * then ascending `x`), each keeping its authored height.
 *
 * Ascending `x` is reading order in BOTH directions, and needs no RTL branch:
 * these are logical coordinates, and `MeasuredResponsiveGrid` mirrors them to
 * physical ones at the RGL seam (`dashboardGrid.rtl.ts`). So `x = 0` renders
 * leftmost in English and rightmost in Hebrew, and is read first either way.
 */
export function buildMobileGridLayout(widgets: ResolvedWidget[]): Layout[] {
  const readingOrder = [...buildGridLayout(widgets)].sort((a, b) => a.y - b.y || a.x - b.x);

  let cursorY = 0;
  return readingOrder.map((item) => {
    const stacked: Layout = {
      i: item.i,
      x: 0,
      y: cursorY,
      w: MOBILE_GRID_COLS,
      h: item.h,
      minW: MOBILE_GRID_COLS,
      minH: WIDGET_MIN_H,
      maxW: MOBILE_GRID_COLS,
    };
    cursorY += item.h;
    return stacked;
  });
}

/**
 * Shrink the cell height of every widget that is currently rendering its empty
 * slot to {@link EMPTY_WIDGET_H}, leaving widgets with data (and those already
 * shorter than the collapsed height) untouched. With `compactType="vertical"`
 * the grid then packs the neighbours upward into the freed space, so an empty
 * dashboard reads compact instead of a column of ~60%-whitespace cards.
 *
 * Read-only render helper only — the caller passes the set of empty widget keys
 * reported at runtime (see `widgetEmptyReporter`); this never touches saved
 * geometry, so a widget's authored height returns the moment it has data.
 */
export function collapseEmptyWidgets(
  layout: Layout[],
  emptyKeys: ReadonlySet<string>
): Layout[] {
  if (emptyKeys.size === 0) return layout;
  let changed = false;
  const next = layout.map((item) => {
    if (emptyKeys.has(item.i) && item.h > EMPTY_WIDGET_H) {
      changed = true;
      return { ...item, h: EMPTY_WIDGET_H };
    }
    return item;
  });
  // Preserve referential identity when no item actually shrank (e.g. an empty
  // key belongs to a different breakpoint's layout, or the item is already
  // short) so RGL isn't handed a fresh layout prop on every render.
  return changed ? next : layout;
}

/**
 * Convert the current RGL layout into save-request placements. Geometry comes
 * from the RGL layout; per-widget `config` is merged back in by key from the
 * editor's widget state (the RGL layout carries only geometry). A widget's
 * non-empty config is persisted so its settings reload verbatim.
 */
export function layoutToPlacements(
  layout: Layout[],
  widgets: ResolvedWidget[]
): WidgetPlacementInput[] {
  const configByKey = new Map(widgets.map((w) => [w.key, w.config]));
  return layout.map((item) => {
    const placement: WidgetPlacementInput = {
      widget_key: item.i,
      x: item.x,
      y: item.y,
      w: item.w,
      h: item.h,
    };
    const config = configByKey.get(item.i);
    if (config && Object.keys(config).length > 0) {
      placement.config = config as WidgetPlacementInput['config'];
    }
    return placement;
  });
}
